import React from "react";

/**
 * Meta / Instagram-style verified badge.
 * 12-point symmetric starburst (outer r=11, inner r=9, center 12,12)
 * with an inset white check. Renders as a perfect circular silhouette.
 *
 * Props:
 *   - size: number | 'xs' | 'sm' | 'md' | 'lg' | 'xl'  (default 'sm' = 14)
 *   - title: tooltip text
 *   - className, testId
 */
const SIZE_PRESETS = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 20,
    xl: 28,
};

// Exact 12-point star: 24 vertices alternating outer r=11, inner r=9,
// starting at top (angle -90°) stepping 15°. Computed symmetrically.
const STAR_PATH =
    "M12 1 L14.33 3.31 L17.5 2.47 L18.36 5.64 L21.53 6.5 L20.69 9.67 " +
    "L23 12 L20.69 14.33 L21.53 17.5 L18.36 18.36 L17.5 21.53 L14.33 20.69 " +
    "L12 23 L9.67 20.69 L6.5 21.53 L5.64 18.36 L2.47 17.5 L3.31 14.33 " +
    "L1 12 L3.31 9.67 L2.47 6.5 L5.64 5.64 L6.5 2.47 L9.67 3.31 Z";

export default function VerifiedBadge({
    size = "sm",
    title = "Verified",
    className = "",
    testId = "verified-badge",
    "data-testid": dataTestId,
}) {
    const px = typeof size === "number" ? size : SIZE_PRESETS[size] || 14;
    const uid = `vb-${px}`;
    const finalTestId = dataTestId || testId;
    return (
        <svg
            viewBox="0 0 24 24"
            width={px}
            height={px}
            // Lock size with inline style so no CSS can stretch it into an oval
            style={{
                width: `${px}px`,
                height: `${px}px`,
                display: "inline-block",
                flexShrink: 0,
                verticalAlign: "middle",
            }}
            aria-label={title}
            role="img"
            className={`shrink-0 ${className}`}
            data-testid={finalTestId}
        >
            <title>{title}</title>
            <defs>
                <linearGradient
                    id={`${uid}-grad`}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="1"
                >
                    <stop offset="0%" stopColor="#2ea6f5" />
                    <stop offset="55%" stopColor="#1d9bf0" />
                    <stop offset="100%" stopColor="#0a77d4" />
                </linearGradient>
            </defs>
            {/* Symmetric 12-point starburst seal */}
            <path d={STAR_PATH} fill={`url(#${uid}-grad)`} />
            {/* White check mark, centered */}
            <path
                d="M8 12 L11 15 L16.5 9"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export { SIZE_PRESETS };
